import { ArrowLeft, Edit, Eye, Filter, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cloudflare } from "@/integrations/cloudflare/client";
import { logError } from "@/lib/env";

export type ProjectStatus = "draft" | "sent" | "viewed" | "accepted" | "rejected" | "scheduled";

interface Project {
  id: string;
  project_name?: string;
  customer_id?: number;
  customer_name?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  budget?: string;
  timeframe?: string;
  description?: string;
  status: ProjectStatus;
  total_quote_value: number;
  created_at: string;
  updated_at: string;
  average_progress?: number;
  total_quotes?: number;
}

interface AllProjectsProps {
  onBack: () => void;
  onNewProject: () => void;
  onViewProject?: (projectId: string) => void;
}

type ApiProject = Partial<Project> & { status?: string };

const statusConfig: Record<
  ProjectStatus,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  draft: { label: "Draft", variant: "outline" },
  sent: { label: "Sent", variant: "default" },
  viewed: { label: "Viewed", variant: "secondary" },
  accepted: { label: "Accepted", variant: "default" },
  rejected: { label: "Rejected", variant: "destructive" },
  scheduled: { label: "Scheduled", variant: "default" },
};

export const AllProjects = ({ onBack, onNewProject, onViewProject }: AllProjectsProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "all">("all");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch all projects with explicit high limit to ensure we get everything
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/projects?limit=1000&offset=0`);
      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }

      const result: { data?: ApiProject[] } = await response.json();
      console.log(`[AllProjects] Fetched ${result.data?.length || 0} projects from API`);

      const normalizedProjects: Project[] = await Promise.all(
        (result.data ?? []).map(async (project) => {
          let averageProgress = 0;
          let totalQuotes = 0;

          // Fetch quotes for this project to calculate progress
          try {
            const quotesResponse = await fetch(
              `${cloudflare.apiUrl}/api/v1/checklist-quotes?project_id=${project.id}`
            );
            if (quotesResponse.ok) {
              const quotesResult = await quotesResponse.json();
              const quotes = quotesResult.data ?? [];
              totalQuotes = quotes.length;

              if (totalQuotes > 0) {
                const totalProgress = quotes.reduce((sum: number, quote: any) => {
                  return sum + (quote.progress_percentage ?? 0);
                }, 0);
                averageProgress = Math.round(totalProgress / totalQuotes);
              }
            }
          } catch (error) {
            console.error(`Failed to fetch quotes for project ${project.id}:`, error);
          }

          return {
            id: project.id ?? "",
            project_name: project.project_name ?? "",
            customer_id: project.customer_id,
            customer_name: project.customer_name,
            first_name: project.first_name ?? "",
            last_name: project.last_name ?? "",
            email: project.email,
            phone: project.phone,
            address: project.address,
            city: project.city,
            budget: project.budget,
            timeframe: project.timeframe,
            description: project.description,
            status: (project.status as ProjectStatus) ?? "draft",
            total_quote_value: project.total_quote_value ?? 0,
            created_at: project.created_at ?? new Date().toISOString(),
            updated_at: project.updated_at ?? new Date().toISOString(),
            average_progress: averageProgress,
            total_quotes: totalQuotes,
          };
        })
      );

      setProjects(normalizedProjects);
    } catch (error) {
      logError("Error loading projects", error instanceof Error ? error : undefined);
      toast({
        title: "Error",
        description: "Failed to load projects.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadProjects().catch((error) => {
      logError("Unhandled error loading projects", error instanceof Error ? error : undefined);
    });
  }, [loadProjects]);

  const filteredProjects = projects.filter((project) => {
    if (statusFilter !== "all" && project.status !== statusFilter) {
      return false;
    }

    if (!searchTerm) {
      return true;
    }

    const search = searchTerm.toLowerCase().trim();

    // Create searchable strings for all project fields
    const projectName = project.project_name || "";
    const customerName =
      project.customer_name || `${project.first_name || ""} ${project.last_name || ""}`.trim();
    const fullName = `${project.first_name || ""} ${project.last_name || ""}`.trim();
    const location = `${project.city || ""} ${project.address || ""}`.trim();

    // Create a comprehensive searchable string
    const searchableFields = [
      projectName,
      customerName,
      fullName,
      project.email || "",
      project.phone || "",
      project.city || "",
      project.address || "",
      project.budget || "",
      project.timeframe || "",
      project.description || "",
      location,
    ]
      .join(" ")
      .toLowerCase();

    // Check if search term matches any field or if all words match
    return (
      searchableFields.includes(search) ||
      search.split(" ").every((word) => searchableFields.includes(word.toLowerCase()))
    );
  });

  // Log filtering results for debugging
  console.log(
    `[AllProjects] Total projects: ${projects.length}, After filtering: ${filteredProjects.length}, Status filter: ${statusFilter}, Search: "${searchTerm}"`
  );

  const handleStatusUpdate = async (projectId: string, newStatus: ProjectStatus) => {
    try {
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/projects/${projectId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update project status: ${response.status}`);
      }

      setProjects((prev) =>
        prev.map((project) =>
          project.id === projectId ? { ...project, status: newStatus } : project
        )
      );

      toast({
        title: "Status Updated",
        description: `Project status changed to ${statusConfig[newStatus].label}`,
      });
    } catch (error) {
      logError("Error updating project status", error instanceof Error ? error : undefined);
      toast({
        title: "Error",
        description: "Failed to update project status.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteProject = async (project: Project) => {
    try {
      // Try to delete via API first
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/projects/${project.id}`, {
        method: "DELETE",
      });

      if (response.status === 404) {
        // Project doesn't exist on server, just remove from UI
        setProjects((prev) => prev.filter((p) => p.id !== project.id));
        setDeletingProject(null);
        toast({
          title: "Project Removed",
          description: "Project removed from view (already deleted from server).",
        });
        return;
      }

      if (!response.ok && response.status !== 204) {
        throw new Error(`Failed to delete project: ${response.status}`);
      }

      // Update local state
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      setDeletingProject(null);

      toast({
        title: "Project Deleted",
        description: `Project for ${project.first_name} ${project.last_name} has been deleted.`,
      });
    } catch (error) {
      logError("Error deleting project", error instanceof Error ? error : undefined);
      toast({
        title: "Error",
        description: "Failed to delete project.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-foreground">All Projects</h1>
        </div>
        <Button onClick={onNewProject}>
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search by project name, customer, location, budget..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as ProjectStatus | "all")}
        >
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(statusConfig).map(([status, config]) => (
              <SelectItem key={status} value={status}>
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Projects List */}
      <Card>
        <CardHeader>
          <CardTitle>Projects ({filteredProjects.length})</CardTitle>
          <CardDescription>Manage and track all your renovation projects</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading projects...</p>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredProjects.map((project) => (
                <Card
                  key={project.id}
                  className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-primary/30"
                  onClick={() => onViewProject?.(project.id)}
                >
                  <CardContent className="p-4">
                    {/* Header with Status */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 pr-2">
                        <h3 className="font-semibold text-base line-clamp-1">
                          {project.project_name ||
                            project.customer_name ||
                            `${project.first_name} ${project.last_name} - Project`}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">ID: {project.id}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/edit-project/${project.id}`);
                          }}
                          title="Edit Project"
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Select
                          value={project.status}
                          onValueChange={(value) => {
                            handleStatusUpdate(project.id, value as ProjectStatus);
                          }}
                        >
                          <SelectTrigger
                            className="w-auto border-none shadow-none p-0 h-auto"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Badge variant={statusConfig[project.status]?.variant || "outline"}>
                              {statusConfig[project.status]?.label || "Unknown"}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(statusConfig).map(([status, config]) => (
                              <SelectItem key={status} value={status}>
                                {config.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {project.customer_name || `${project.first_name} ${project.last_name}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {project.email || "No email"}
                          </p>
                        </div>
                      </div>

                      {/* Location */}
                      {(project.city || project.address) && (
                        <div className="text-xs">
                          <span className="font-medium">Location:</span> {project.city || "N/A"}
                          {project.address && (
                            <span className="text-muted-foreground block truncate">
                              {project.address}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Description */}
                      {project.description && (
                        <div className="text-xs pt-2 border-t">
                          <span className="font-medium">Description:</span>
                          <p className="text-muted-foreground mt-1 line-clamp-2">
                            {project.description}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {project.total_quotes !== undefined && project.total_quotes > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                          <span>Overall Progress</span>
                          <span className="font-medium">{project.average_progress ?? 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                            style={{ width: `${project.average_progress ?? 0}%` }}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {project.total_quotes} quote{project.total_quotes !== 1 ? "s" : ""}
                        </div>
                      </div>
                    )}

                    {/* Budget and Timeline */}
                    <div className="grid grid-cols-2 gap-2 mb-3 pt-3 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground">Budget</p>
                        <p className="text-sm font-medium">{project.budget || "Not set"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Timeline</p>
                        <p className="text-sm font-medium">{project.timeframe || "Not set"}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between items-center pt-3 border-t">
                      <p className="text-xs text-muted-foreground">
                        Created: {new Date(project.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            onViewProject?.(project.id);
                          }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/edit-project/${project.id}`);
                          }}
                          title="Edit Project"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {project.status === "draft" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setDeletingProject(project);
                            }}
                            className="text-destructive hover:text-destructive"
                            title="Delete Project"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!loading && filteredProjects.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No projects found matching your criteria.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Preview Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="relative">
            <DialogTitle>
              {selectedProject?.project_name ||
                selectedProject?.customer_name ||
                `${selectedProject?.first_name} ${selectedProject?.last_name} - Project`}
            </DialogTitle>
            <DialogDescription>
              Project ID: {selectedProject?.id} • View complete project information and summary
            </DialogDescription>
            <div className="absolute top-0 right-0">
              <Badge variant={statusConfig[selectedProject?.status || "new"]?.variant || "outline"}>
                {statusConfig[selectedProject?.status || "new"]?.label || "New"}
              </Badge>
            </div>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Customer Information</h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Customer:</strong>{" "}
                      {selectedProject.customer_name ||
                        `${selectedProject.first_name} ${selectedProject.last_name}`}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedProject.email || "N/A"}
                    </p>
                    <p>
                      <strong>Phone:</strong> {selectedProject.phone || "N/A"}
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Project Information</h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Project Name:</strong>{" "}
                      {selectedProject.project_name || "No name specified"}
                    </p>
                    <p>
                      <strong>Location:</strong> {selectedProject.city || "N/A"}
                    </p>
                    <p>
                      <strong>Address:</strong> {selectedProject.address || "N/A"}
                    </p>
                    <p>
                      <strong>Budget:</strong> {selectedProject.budget || "N/A"}
                    </p>
                    <p>
                      <strong>Timeframe:</strong> {selectedProject.timeframe || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-sm text-muted-foreground">
                  {selectedProject.description || "No description provided"}
                </p>
              </div>

              <div className="text-center p-4 bg-primary/10 rounded-lg border">
                <h4 className="font-semibold mb-2 text-primary">Quote Value</h4>
                <p className="text-3xl font-bold text-primary bg-primary/20 px-4 py-2 rounded-lg inline-block">
                  ${selectedProject.total_quote_value?.toFixed(2) || "0.00"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="text-sm">
                    {new Date(selectedProject.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Modified</p>
                  <p className="text-sm">
                    {new Date(selectedProject.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingProject} onOpenChange={() => setDeletingProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the project for {deletingProject?.first_name}{" "}
              {deletingProject?.last_name}? This action cannot be undone and will also delete all
              associated project items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingProject && handleDeleteProject(deletingProject)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
